import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useAstralInfo } from '../hooks/useAstralInfo';
import { useSpiritualStats } from '../hooks/useSpiritualStats';
import { moodService } from '../services/api';
import FullAstralMap from './FullAstralMap';
import HoroscopoDoDiaCard from './spiritual/HoroscopoDoDiaCard';
import TransitoPlanetario from './spiritual/TransitoPlanetario';
import RetrogradosAtivos from './spiritual/RetrogradosAtivos';
import { firstName, agree, Gender } from '../utils/gender';
import { allTarotCards } from '../data/tarotCards';
import {
  SparklesIcon,
  SunIcon,
  StarIcon,
  FaceSmileIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

interface MoodOption {
  id: string;
  m: string;
  f: string;
  neutral: string;
  icon: string;
  color: string;
}

const SpiritualDashboard: React.FC = () => {
  const { user } = useAuth();
  const gender = (user?.profile as any)?.gender as Gender;
  const first = firstName(user?.name);
  const [selectedMood, setSelectedMood] = useState<string>('');
  const [dailyCard, setDailyCard] = useState<any>(null);
  const [lastCardDate, setLastCardDate] = useState<string>('');
  const [canGenerateNewCard, setCanGenerateNewCard] = useState<boolean>(true);
  const [showFullAstralMap, setShowFullAstralMap] = useState<boolean>(false);
  const [achievementToast, setAchievementToast] = useState<{ icon: string; label: string; tokens: number } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { astralInfo, birthChart, reload: reloadAstralInfo } = useAstralInfo();
  const { newAchievements } = useSpiritualStats();

  // Colunas 2 e 3 (Mapa Astral+Retrógrados, Horóscopo+Trânsito) acompanham a altura
  // real da Coluna 1 (Humor+Carta do Dia) em telas grandes — o CSS Grid sozinho não
  // resolve isso (uma linha "auto" sempre cresce para caber o conteúdo mais alto,
  // não limita as demais colunas), por isso medimos via ResizeObserver.
  const col1Ref = useRef<HTMLDivElement>(null);
  const [col1Height, setCol1Height] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = col1Ref.current;
    if (!el) return;
    const LG_BREAKPOINT = 1024; // Tailwind "lg"
    const update = () => {
      setCol1Height(window.innerWidth >= LG_BREAKPOINT ? el.getBoundingClientRect().height : undefined);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    // Reforço: a imagem da Carta do Dia e o dado do dailyCard chegam de forma
    // assíncrona depois da primeira medição, e nem sempre disparam o
    // ResizeObserver a tempo — reconfere algumas vezes logo após montar.
    const retries = [300, 800, 1500, 3000].map(delay => setTimeout(update, delay));
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      retries.forEach(clearTimeout);
    };
  }, []);

  const moodOptions: MoodOption[] = [
    { id: 'grateful',    m: 'Grato',        f: 'Grata',        neutral: 'Com gratidão',  icon: '🙏', color: 'from-green-400 to-emerald-500' },
    { id: 'peaceful',    m: 'Tranquilo',    f: 'Tranquila',    neutral: 'Em paz',         icon: '🕊️', color: 'from-blue-400 to-cyan-500' },
    { id: 'energetic',   m: 'Energético',   f: 'Energética',   neutral: 'Com energia',    icon: '⚡', color: 'from-yellow-400 to-orange-500' },
    { id: 'reflective',  m: 'Reflexivo',    f: 'Reflexiva',    neutral: 'Em reflexão',    icon: '🤔', color: 'from-purple-400 to-indigo-500' },
    { id: 'anxious',     m: 'Ansioso',      f: 'Ansiosa',      neutral: 'Com ansiedade',  icon: '😰', color: 'from-red-400 to-pink-500' },
    { id: 'hopeful',     m: 'Esperançoso',  f: 'Esperançosa',  neutral: 'Com esperança',  icon: '🌟', color: 'from-gold-400 to-yellow-500' },
    { id: 'sad',         m: 'Triste',       f: 'Triste',       neutral: 'Triste',         icon: '😢', color: 'from-slate-400 to-blue-600' },
    { id: 'excited',     m: 'Animado',      f: 'Animada',      neutral: 'Com empolgação', icon: '🎉', color: 'from-pink-400 to-rose-500' },
    { id: 'tired',       m: 'Cansado',      f: 'Cansada',      neutral: 'Com cansaço',    icon: '😴', color: 'from-gray-400 to-slate-500' },
    { id: 'overwhelmed', m: 'Sobrecarregado', f: 'Sobrecarregada', neutral: 'Em sobrecarga', icon: '😵', color: 'from-orange-400 to-red-500' },
    { id: 'loving',      m: 'Amoroso',      f: 'Amorosa',      neutral: 'Com amor',       icon: '💖', color: 'from-rose-400 to-pink-600' },
    { id: 'courageous',  m: 'Corajoso',     f: 'Corajosa',     neutral: 'Com coragem',    icon: '🦁', color: 'from-amber-400 to-orange-500' },
  ];
  const moodLabel = (m: MoodOption) => agree(gender, { m: m.m, f: m.f, neutral: m.neutral });

  // Semente determinística: muda por dia + por usuário
  const getDaySeed = (extra = 0) => {
    const d = new Date();
    const dateVal = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    const userId = user?.id || 0;
    return (dateVal * 31 + userId * 17 + extra * 7) >>> 0;
  };
  const pickFrom = <T,>(arr: T[], seed: number): T => arr[seed % arr.length];

  const CDN = 'https://res.cloudinary.com/dnkklol3i/image/upload/w_800,c_limit,f_auto,q_auto/tarot/arcanos_maiores';

  // 22 Arcanos Maiores
  const MAJOR_ARCANA = [
    { name: 'O Louco',          symbol: '🌟', meaning: 'Uma nova jornada se abre. Confie no universo e dê o primeiro passo sem medo.',                                   energy: 'Aventura',      image: `${CDN}/olouco.png` },
    { name: 'O Mago',           symbol: '✨', meaning: 'Você tem todos os recursos para manifestar seus desejos. A vontade cria realidade.',                              energy: 'Manifestação',  image: `${CDN}/omago.png` },
    { name: 'A Sacerdotisa',    symbol: '🌙', meaning: 'Mergulhe em sua intuição. As respostas que busca estão dentro de você.',                                          energy: 'Intuição',      image: `${CDN}/asacerdotisa.png` },
    { name: 'A Imperatriz',     symbol: '🌺', meaning: 'Abundância e fertilidade fluem em sua vida. Cuide de si e do que ama.',                                           energy: 'Abundância',    image: `${CDN}/aimperatriz.png` },
    { name: 'O Imperador',      symbol: '👑', meaning: 'Estruture seus planos com firmeza. Disciplina e organização abrem caminhos.',                                     energy: 'Estrutura',     image: `${CDN}/oimperador.png` },
    { name: 'O Hierofante',     symbol: '🔯', meaning: 'Busque sabedoria em tradições e mentores. O conhecimento coletivo ilumina.',                                      energy: 'Sabedoria',     image: `${CDN}/ohierofante.png` },
    { name: 'Os Amantes',       symbol: '💞', meaning: 'Uma escolha importante se aproxima. Siga o coração em harmonia com a mente.',                                     energy: 'Harmonia' },
    { name: 'O Carro',          symbol: '🏆', meaning: 'Vitória através da determinação. Mantenha o foco e conduza sua vida com propósito.',                              energy: 'Vitória',       image: `${CDN}/ocarro.png` },
    { name: 'A Força',          symbol: '🦁', meaning: 'Sua força interior supera qualquer obstáculo. Aja com coragem e compaixão.',                                      energy: 'Coragem',       image: `${CDN}/aforca.png` },
    { name: 'O Eremita',        symbol: '🕯️', meaning: 'Um momento de introspecção e silêncio. A solidão traz clareza e autoconhecimento.',                               energy: 'Reflexão',      image: `${CDN}/oeremita.png` },
    { name: 'A Roda da Fortuna',symbol: '🎡', meaning: 'Os ciclos se movem a seu favor. Aceite as mudanças e flua com a vida.',                                           energy: 'Mudança',       image: `${CDN}/arodadafortuna.png` },
    { name: 'A Justiça',        symbol: '⚖️', meaning: 'Equilíbrio e verdade se manifestam. O que foi semeado, agora é colhido.',                                         energy: 'Equilíbrio',    image: `${CDN}/ajustica.png` },
    { name: 'O Enforcado',      symbol: '🌀', meaning: 'Pause e mude a perspectiva. Às vezes render-se é o caminho para a libertação.',                                   energy: 'Entrega',       image: `${CDN}/oenforcado.png` },
    { name: 'A Morte',          symbol: '🦋', meaning: 'Uma transformação profunda se inicia. Deixe o velho ir para o novo florescer.',                                   energy: 'Transformação', image: `${CDN}/amorte.png` },
    { name: 'A Temperança',     symbol: '🌊', meaning: 'Paciência e equilíbrio são seus aliados. Integre opostos com calma e graça.',                                     energy: 'Paciência',     image: `${CDN}/atemperanca.png` },
    { name: 'O Diabo',          symbol: '🔗', meaning: 'Observe o que te prende. A consciência sobre padrões é o primeiro passo para libertá-los.',                      energy: 'Consciência',   image: `${CDN}/odiabo.png` },
    { name: 'A Torre',          symbol: '⚡', meaning: 'Uma revelação repentina abala estruturas antigas. O que ruiu abre espaço para o verdadeiro.',                     energy: 'Revelação',     image: `${CDN}/atorre.png` },
    { name: 'A Estrela',        symbol: '⭐', meaning: 'Esperança e renovação iluminam seu caminho. Confie na proteção do universo.',                                     energy: 'Esperança',     image: `${CDN}/aestrela.png` },
    { name: 'A Lua',            symbol: '🌕', meaning: 'Confie em sua intuição mesmo na incerteza. Nem tudo precisa ser explicado pela razão.',                           energy: 'Mistério',      image: `${CDN}/alua.png` },
    { name: 'O Sol',            symbol: '☀️', meaning: 'Alegria, clareza e sucesso irradiam de você hoje. Celebre a vida com autenticidade.',                             energy: 'Alegria' },
    { name: 'O Julgamento',     symbol: '🎺', meaning: 'Um chamado para despertar. É hora de responder à sua missão com coragem.',                                        energy: 'Despertar',     image: `${CDN}/ojulgamento.png` },
    { name: 'O Mundo',          symbol: '🌍', meaning: 'Completude e realização. Você está exatamente onde precisa estar.',                                               energy: 'Plenitude',     image: `${CDN}/omundo.png` },
  ];

  // Arcanos Menores (56 cartas) — pedido do usuário pra Carta do Dia sortear entre
  // o baralho completo, não só os Maiores. Sem arte própria pra Menores em lugar
  // nenhum do app (nem aqui nem no catálogo real de tiragens) — usa a mesma fonte
  // compartilhada de `tarotCards.ts` (meaning.upright + 1º keyword como "energia"),
  // e a imagem fica undefined de propósito: o fallback abaixo mostra o nome da
  // carta num cartão estilizado, igual ao que já acontece numa tiragem de verdade
  // (CardSelectionGrid) quando a carta sorteada não tem imagem.
  const MINOR_ARCANA = allTarotCards
    .filter(c => c.arcana === 'minor')
    .map(c => ({
      name: c.name,
      symbol: undefined as string | undefined,
      meaning: c.meaning.upright,
      energy: c.keywords[0] ? c.keywords[0].charAt(0).toUpperCase() + c.keywords[0].slice(1) : 'Intuição',
      image: c.image,
    }));

  const DAILY_CARD_POOL = [...MAJOR_ARCANA, ...MINOR_ARCANA];

  // Mensagem de boas-vindas determinística por signo + dia
  const getWelcomeMessage = () => {
    const sign = astralInfo?.sign || '';
    const element = astralInfo?.element || '';
    const messages = [
      `As energias de ${sign || 'hoje'} estão favoráveis. Confie no que seu coração diz.`,
      `Hoje é um dia especial para ${sign || 'você'}. O universo conspira ao seu favor.`,
      `Como ${element || 'ser único'} que você é, sua intuição está aguçada hoje.`,
      `${sign ? `A força de ${sign}` : 'Sua força interior'} te acompanha em cada passo.`,
      `O cosmos te convida à introspecção e ao crescimento genuíno hoje.`,
      `Você é exatamente quem precisa ser. Confie no seu caminho único.`,
      `Novas perspectivas chegam para quem está aberto a recebê-las.`,
    ];
    return pickFrom(messages, getDaySeed(0));
  };

  useEffect(() => {
    // Simular carregamento de dados do usuário
    checkDailyCard();
    loadTodayMood();
  }, []);

  // Toast de conquista desbloqueada (baseado nas conquistas novas retornadas pelo hook de stats)
  useEffect(() => {
    if (newAchievements.length > 0) {
      const best = newAchievements.reduce((a, b) => (b.tokens > a.tokens ? b : a));
      const totalTokens = newAchievements.reduce((sum, a) => sum + a.tokens, 0);
      setAchievementToast({ icon: best.icon, label: best.label, tokens: totalTokens });
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setAchievementToast(null), 4500);
    }
  }, [newAchievements]);

  const checkDailyCard = () => {
    const today = new Date().toDateString();
    const storedCardDate = localStorage.getItem('lastCardDate');
    const storedCard = localStorage.getItem('dailyCard');

    if (storedCardDate === today && storedCard) {
      // Usar carta armazenada se for do mesmo dia
      setDailyCard(JSON.parse(storedCard));
      setLastCardDate(storedCardDate);
      setCanGenerateNewCard(false);
    } else {
      // Gerar nova carta se for um novo dia
      generateDailyCard();
      setCanGenerateNewCard(true);
    }
  };

  const generateDailyCard = async () => {
    const today = new Date();
    const todayString = today.toDateString();
    // Semente única por usuário + dia — garante cartas diferentes para cada pessoa
    const seed = getDaySeed(99);
    const selectedCard = DAILY_CARD_POOL[seed % DAILY_CARD_POOL.length];

    setDailyCard(selectedCard);
    setLastCardDate(todayString);
    setCanGenerateNewCard(false);
    localStorage.setItem('dailyCard', JSON.stringify(selectedCard));
    localStorage.setItem('lastCardDate', todayString);
    await reloadAstralInfo();
  };

  const handleMoodSelect = (moodId: string) => {
    setSelectedMood(moodId);
    moodService.setTodayMood(moodId).catch((err) => {
      console.error('Erro ao salvar humor:', err);
    });
  };

  const loadTodayMood = async () => {
    try {
      const res = await moodService.getTodayMood();
      setSelectedMood(res.mood);
    } catch (e) {
      // Sem humor registrado hoje
    }
  };

  return (
    <div className="space-y-6">

      {/* Toast de conquista desbloqueada */}
      <AnimatePresence>
        {achievementToast && (
          <motion.div
            key="achievement-toast"
            initial={{ opacity: 0, y: -60, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -40, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            className="fixed top-4 left-1/2 z-50 -translate-x-1/2 pointer-events-none"
          >
            <div
              className="flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(30,18,60,0.97) 0%, rgba(20,10,45,0.97) 100%)',
                border: '1px solid rgba(251,191,36,0.5)',
                boxShadow: '0 8px 32px rgba(251,191,36,0.25)',
              }}
            >
              <span className="text-2xl">{achievementToast.icon}</span>
              <div>
                <p className="text-xs text-gold-400 font-semibold tracking-wide uppercase">Conquista desbloqueada!</p>
                <p className="text-white font-bold text-sm leading-tight">{achievementToast.label}</p>
              </div>
              {achievementToast.tokens > 0 && (
                <div
                  className="ml-2 px-2.5 py-1 rounded-full text-xs font-bold"
                  style={{ background: 'rgba(251,191,36,0.18)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.35)' }}
                >
                  +{achievementToast.tokens} token{achievementToast.tokens > 1 ? 's' : ''}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Boas-vindas personalizadas */}
      <div className="backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20" style={{ background: 'rgba(255,255,255,0.05)' }}>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-r from-mystical-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
            <SparklesIcon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base sm:text-xl font-mystical font-bold text-white leading-tight">
              {agree(gender, { m: 'Bem-vindo', f: 'Bem-vinda', neutral: 'Olá' })} de volta, {first}! <span className="text-sm">✨</span>
            </h2>
            <p className="text-gray-300 text-sm mt-0.5 leading-snug">{getWelcomeMessage()}</p>
          </div>
        </div>
      </div>

      {/* Grid principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Coluna 1: Humor e Carta do Dia — referência de altura pras colunas 2 e 3 */}
        <div ref={col1Ref} className="space-y-6">

          {/* Registro de Humor */}
          <div className="backdrop-blur-sm rounded-xl p-6 border border-white/20" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <div className="flex items-center space-x-2 mb-4">
              <FaceSmileIcon className="w-6 h-6 text-mystical-400" />
              <h3 className="text-lg font-semibold text-white">Como você está se sentindo?</h3>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {moodOptions.map((mood) => (
                <button
                  key={mood.id}
                  onClick={() => handleMoodSelect(mood.id)}
                  disabled={!!selectedMood}
                  className={`p-2 rounded-xl border transition-all duration-200 ${
                    selectedMood === mood.id
                      ? `bg-gradient-to-r ${mood.color} border-white/30 scale-105`
                      : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                >
                  <div className="text-xl mb-0.5">{mood.icon}</div>
                  <div className="text-xs text-white font-medium leading-tight">{moodLabel(mood)}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Carta do Dia */}
          <div className="backdrop-blur-sm rounded-xl p-6 border border-white/20" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <StarIcon className="w-6 h-6 text-gold-400" />
                <h3 className="text-lg font-semibold text-white">Carta do Dia</h3>
              </div>
              {!canGenerateNewCard && (
                <div className="text-xs text-gray-400 bg-gray-700/50 px-2 py-1 rounded-full">
                  Nova carta em {Math.ceil((24 - new Date().getHours()))} horas
                </div>
              )}
            </div>

            {dailyCard && (() => {
              // Busca a imagem atualizada pelo nome, independente do que está no localStorage
              const cardData = DAILY_CARD_POOL.find(c => c.name === dailyCard.name);
              const cardImage = cardData?.image || dailyCard.image;
              return (
                <div className="text-center">
                  <div className="w-48 mx-auto mb-4 rounded-xl overflow-hidden border-2 border-gold-400/40 shadow-lg shadow-gold-500/20">
                    {cardImage ? (
                      <img src={cardImage} alt={dailyCard.name} className="w-full object-contain" />
                    ) : (
                      // Sem arte (comum pros Arcanos Menores) — mesmo tratamento de
                      // "carta com nome" usado numa tiragem de verdade, em vez de um
                      // ícone genérico. aspect-[2/3] dá altura própria à caixa (sem
                      // imagem irmã pra "emprestar" altura ao container).
                      <div className="w-full aspect-[2/3] bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 flex items-center justify-center p-3">
                        <span className="text-gold-300 font-bold text-center leading-tight">{dailyCard.name}</span>
                      </div>
                    )}
                  </div>
                  <h4 className="text-xl font-mystical font-bold text-gold-400 mb-2">
                    {dailyCard.name}
                  </h4>
                  <p className="text-gray-300 text-sm mb-3 leading-relaxed">{dailyCard.meaning}</p>
                  <span className="inline-block px-3 py-1 bg-gold-400/15 text-gold-300 rounded-full text-xs border border-gold-400/20">
                    ✦ {dailyCard.energy}
                  </span>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Coluna 2: Mapa Astral — em telas grandes usa a altura medida da Coluna 1
            (Carta do Dia), e o Retrógrados Ativos absorve o espaço extra com rolagem
            interna, em vez do card crescer e desalinhar as colunas. */}
        <div
          className="flex flex-col gap-6 lg:min-h-0"
          style={col1Height ? { height: col1Height } : undefined}
        >

          {/* Mapa Astral */}
          <div className="backdrop-blur-sm rounded-xl p-6 border border-white/20 flex-shrink-0" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <SunIcon className="w-6 h-6 text-orange-400" />
                <h3 className="text-lg font-semibold text-white">Seu Mapa Astral</h3>
              </div>
              <button
                onClick={() => {
                  setShowFullAstralMap(true);
                }}
                className="flex items-center space-x-2 px-3 py-2 bg-orange-400/20 hover:bg-orange-400/30 text-orange-400 rounded-lg transition-colors text-sm"
              >
                <EyeIcon className="w-4 h-4" />
                <span>Ver Mapa Completo</span>
              </button>
            </div>

            {astralInfo && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Signo Solar:</span>
                  <span className="text-orange-400 font-semibold">{astralInfo.sign}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Elemento:</span>
                  <span className="text-red-400 font-semibold">{astralInfo.element}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Planeta Regente:</span>
                  <span className="text-yellow-400 font-semibold">{astralInfo.planet}</span>
                </div>
                {birthChart?.ascendant && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Ascendente:</span>
                    <span className="text-orange-300 font-semibold">{birthChart.ascendant}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Fase Lunar:</span>
                  <span className="text-blue-400 font-semibold">{astralInfo.moonPhase}</span>
                </div>

                <div className="mt-4 p-3 bg-orange-400/10 rounded-lg">
                  <p className="text-sm text-gray-300">{astralInfo.dailyMessage}</p>
                </div>
              </div>
            )}
          </div>

          <RetrogradosAtivos />
        </div>

        {/* Coluna 3: Horóscopo do Dia e Trânsito — mesmo esquema da Coluna 2 */}
        <div
          className="flex flex-col gap-6 lg:min-h-0"
          style={col1Height ? { height: col1Height } : undefined}
        >
          <div className="flex-shrink-0">
            <HoroscopoDoDiaCard sign={astralInfo?.sign || null} />
          </div>
          <TransitoPlanetario />
        </div>
      </div>

      {/* Modal do Mapa Astral Completo */}
      <FullAstralMap
        isOpen={showFullAstralMap}
        onClose={() => setShowFullAstralMap(false)}
        astralData={astralInfo}
        birthChart={birthChart}
      />
    </div>
  );
};

export default SpiritualDashboard;
